import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
  MarketingCampaign,
  CampaignType,
  CampaignStatus,
  EmailCampaign,
  EmailStatus,
  EmailTemplate,
  ContentPost,
  Event,
  LeadSourceRecord,
} from '@/models/marketing.model';

const CAMPAIGNS_COLLECTION = 'marketingCampaigns';
const EMAIL_CAMPAIGNS_COLLECTION = 'emailCampaigns';
const EMAIL_TEMPLATES_COLLECTION = 'emailTemplates';
const CONTENT_POSTS_COLLECTION = 'contentPosts';
const EVENTS_COLLECTION = 'marketingEvents';
const LEAD_SOURCES_COLLECTION = 'leadSources';

export class MarketingService {
  // ========== Marketing Campaigns ==========

  static async getAllCampaigns(): Promise<MarketingCampaign[]> {
    const q = query(
      collection(db, CAMPAIGNS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToCampaign(doc.id, doc.data()));
  }

  static async getCampaignById(id: string): Promise<MarketingCampaign | null> {
    const docRef = doc(db, CAMPAIGNS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return null;
    }
    return this.docToCampaign(docSnap.id, docSnap.data());
  }

  static async filterCampaignsByStatus(status: CampaignStatus): Promise<MarketingCampaign[]> {
    const q = query(
      collection(db, CAMPAIGNS_COLLECTION),
      where('status', '==', status),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToCampaign(doc.id, doc.data()));
  }

  static async createCampaign(campaignData: Partial<MarketingCampaign>): Promise<string> {
    const now = Timestamp.now();
    const data: any = {
      name: campaignData.name || '',
      type: campaignData.type || CampaignType.email,
      status: campaignData.status || CampaignStatus.draft,
      description: campaignData.description || null,
      startDate: campaignData.startDate ? Timestamp.fromDate(campaignData.startDate) : null,
      endDate: campaignData.endDate ? Timestamp.fromDate(campaignData.endDate) : null,
      budget: campaignData.budget || null,
      spent: campaignData.spent || 0,
      targetAudience: campaignData.targetAudience || null,
      goals: campaignData.goals || [],
      metrics: campaignData.metrics || {},
      createdBy: campaignData.createdBy || '',
      createdByName: campaignData.createdByName || '',
      createdAt: now,
      lastUpdated: now,
    };

    const docRef = await addDoc(collection(db, CAMPAIGNS_COLLECTION), data);
    return docRef.id;
  }

  static async updateCampaign(
    id: string,
    updates: Partial<MarketingCampaign>
  ): Promise<void> {
    const docRef = doc(db, CAMPAIGNS_COLLECTION, id);
    const updateData: any = {
      lastUpdated: Timestamp.now(),
    };

    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.spent !== undefined) updateData.spent = updates.spent;
    if (updates.metrics !== undefined) updateData.metrics = updates.metrics;
    if (updates.startDate !== undefined) updateData.startDate = updates.startDate ? Timestamp.fromDate(updates.startDate) : null;
    if (updates.endDate !== undefined) updateData.endDate = updates.endDate ? Timestamp.fromDate(updates.endDate) : null;

    await updateDoc(docRef, updateData);
  }

  // ========== Email Campaigns ==========

  static async getAllEmailCampaigns(): Promise<EmailCampaign[]> {
    const q = query(
      collection(db, EMAIL_CAMPAIGNS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToEmailCampaign(doc.id, doc.data()));
  }

  static async getEmailCampaignById(id: string): Promise<EmailCampaign | null> {
    const docRef = doc(db, EMAIL_CAMPAIGNS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return null;
    }
    return this.docToEmailCampaign(docSnap.id, docSnap.data());
  }

  static async createEmailCampaign(campaignData: Partial<EmailCampaign>): Promise<string> {
    const now = Timestamp.now();
    const data: any = {
      campaignId: campaignData.campaignId || null,
      name: campaignData.name || '',
      subject: campaignData.subject || '',
      fromEmail: campaignData.fromEmail || '',
      fromName: campaignData.fromName || '',
      templateId: campaignData.templateId || null,
      htmlContent: campaignData.htmlContent || '',
      textContent: campaignData.textContent || null,
      status: campaignData.status || EmailStatus.draft,
      scheduledAt: campaignData.scheduledAt ? Timestamp.fromDate(campaignData.scheduledAt) : null,
      sentAt: campaignData.sentAt ? Timestamp.fromDate(campaignData.sentAt) : null,
      recipientCount: campaignData.recipientCount || 0,
      deliveredCount: campaignData.deliveredCount || 0,
      openedCount: campaignData.openedCount || 0,
      clickedCount: campaignData.clickedCount || 0,
      bouncedCount: campaignData.bouncedCount || 0,
      unsubscribedCount: campaignData.unsubscribedCount || 0,
      segments: campaignData.segments || [],
      tags: campaignData.tags || [],
      createdBy: campaignData.createdBy || '',
      createdByName: campaignData.createdByName || '',
      createdAt: now,
      lastUpdated: now,
    };

    const docRef = await addDoc(collection(db, EMAIL_CAMPAIGNS_COLLECTION), data);
    return docRef.id;
  }

  // ========== Email Templates ==========

  static async getAllEmailTemplates(): Promise<EmailTemplate[]> {
    const q = query(
      collection(db, EMAIL_TEMPLATES_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToEmailTemplate(doc.id, doc.data()));
  }

  static async getActiveEmailTemplates(): Promise<EmailTemplate[]> {
    const q = query(
      collection(db, EMAIL_TEMPLATES_COLLECTION),
      where('isActive', '==', true),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToEmailTemplate(doc.id, doc.data()));
  }

  static async getEmailTemplateById(id: string): Promise<EmailTemplate | null> {
    const docRef = doc(db, EMAIL_TEMPLATES_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return null;
    }
    return this.docToEmailTemplate(docSnap.id, docSnap.data());
  }

  static async createEmailTemplate(templateData: Partial<EmailTemplate>): Promise<string> {
    const now = Timestamp.now();
    const data: any = {
      name: templateData.name || '',
      subject: templateData.subject || '',
      htmlContent: templateData.htmlContent || '',
      textContent: templateData.textContent || null,
      category: templateData.category || null,
      variables: templateData.variables || [],
      isActive: templateData.isActive !== undefined ? templateData.isActive : true,
      createdBy: templateData.createdBy || '',
      createdByName: templateData.createdByName || '',
      createdAt: now,
      lastUpdated: now,
    };

    const docRef = await addDoc(collection(db, EMAIL_TEMPLATES_COLLECTION), data);
    return docRef.id;
  }

  static async updateEmailTemplate(id: string, updates: Partial<EmailTemplate>): Promise<void> {
    const docRef = doc(db, EMAIL_TEMPLATES_COLLECTION, id);
    const updateData: any = {
      lastUpdated: Timestamp.now(),
    };

    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.subject !== undefined) updateData.subject = updates.subject;
    if (updates.htmlContent !== undefined) updateData.htmlContent = updates.htmlContent;
    if (updates.textContent !== undefined) updateData.textContent = updates.textContent;
    if (updates.category !== undefined) updateData.category = updates.category;
    if (updates.variables !== undefined) updateData.variables = updates.variables;
    if (updates.isActive !== undefined) updateData.isActive = updates.isActive;

    await updateDoc(docRef, updateData);
  }

  // ========== Content Posts ==========

  static async getAllContentPosts(): Promise<ContentPost[]> {
    const q = query(
      collection(db, CONTENT_POSTS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToContentPost(doc.id, doc.data()));
  }

  static async getContentPostById(id: string): Promise<ContentPost | null> {
    const docRef = doc(db, CONTENT_POSTS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return null;
    }
    return this.docToContentPost(docSnap.id, docSnap.data());
  }

  static async createContentPost(postData: Partial<ContentPost>): Promise<string> {
    const now = Timestamp.now();
    const data: any = {
      title: postData.title || '',
      type: postData.type || 'blog',
      content: postData.content || null,
      url: postData.url || null,
      platform: postData.platform || null,
      status: postData.status || 'draft',
      scheduledAt: postData.scheduledAt ? Timestamp.fromDate(postData.scheduledAt) : null,
      publishedAt: postData.publishedAt ? Timestamp.fromDate(postData.publishedAt) : null,
      author: postData.author || '',
      authorName: postData.authorName || '',
      tags: postData.tags || [],
      metrics: postData.metrics || {},
      createdAt: now,
      lastUpdated: now,
    };

    const docRef = await addDoc(collection(db, CONTENT_POSTS_COLLECTION), data);
    return docRef.id;
  }

  static async updateContentPost(id: string, updates: Partial<ContentPost>): Promise<void> {
    const docRef = doc(db, CONTENT_POSTS_COLLECTION, id);
    const updateData: any = {
      lastUpdated: Timestamp.now(),
    };

    if (updates.title !== undefined) updateData.title = updates.title;
    if (updates.type !== undefined) updateData.type = updates.type;
    if (updates.content !== undefined) updateData.content = updates.content;
    if (updates.url !== undefined) updateData.url = updates.url;
    if (updates.platform !== undefined) updateData.platform = updates.platform;
    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.scheduledAt !== undefined) updateData.scheduledAt = updates.scheduledAt ? Timestamp.fromDate(updates.scheduledAt) : null;
    if (updates.publishedAt !== undefined) updateData.publishedAt = updates.publishedAt ? Timestamp.fromDate(updates.publishedAt) : null;
    if (updates.tags !== undefined) updateData.tags = updates.tags;
    if (updates.metrics !== undefined) updateData.metrics = updates.metrics;

    await updateDoc(docRef, updateData);
  }

  // ========== Events ==========

  static async getAllEvents(): Promise<Event[]> {
    const q = query(
      collection(db, EVENTS_COLLECTION),
      orderBy('startDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToEvent(doc.id, doc.data()));
  }

  static async getEventById(id: string): Promise<Event | null> {
    const docRef = doc(db, EVENTS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return null;
    }
    return this.docToEvent(docSnap.id, docSnap.data());
  }

  static async createEvent(eventData: Partial<Event>): Promise<string> {
    const now = Timestamp.now();
    const data: any = {
      name: eventData.name || '',
      type: eventData.type || 'other',
      description: eventData.description || null,
      startDate: eventData.startDate ? Timestamp.fromDate(eventData.startDate) : now,
      endDate: eventData.endDate ? Timestamp.fromDate(eventData.endDate) : now,
      location: eventData.location || null,
      virtualUrl: eventData.virtualUrl || null,
      registrationUrl: eventData.registrationUrl || null,
      budget: eventData.budget || null,
      spent: eventData.spent || 0,
      registrations: eventData.registrations || 0,
      attendees: eventData.attendees || 0,
      leads: eventData.leads || 0,
      status: eventData.status || 'planned',
      createdBy: eventData.createdBy || '',
      createdByName: eventData.createdByName || '',
      createdAt: now,
      lastUpdated: now,
    };

    const docRef = await addDoc(collection(db, EVENTS_COLLECTION), data);
    return docRef.id;
  }

  static async updateEvent(id: string, updates: Partial<Event>): Promise<void> {
    const docRef = doc(db, EVENTS_COLLECTION, id);
    const updateData: any = {
      lastUpdated: Timestamp.now(),
    };

    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.type !== undefined) updateData.type = updates.type;
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.startDate !== undefined) updateData.startDate = updates.startDate ? Timestamp.fromDate(updates.startDate) : null;
    if (updates.endDate !== undefined) updateData.endDate = updates.endDate ? Timestamp.fromDate(updates.endDate) : null;
    if (updates.location !== undefined) updateData.location = updates.location;
    if (updates.virtualUrl !== undefined) updateData.virtualUrl = updates.virtualUrl;
    if (updates.registrationUrl !== undefined) updateData.registrationUrl = updates.registrationUrl;
    if (updates.budget !== undefined) updateData.budget = updates.budget;
    if (updates.spent !== undefined) updateData.spent = updates.spent;
    if (updates.registrations !== undefined) updateData.registrations = updates.registrations;
    if (updates.attendees !== undefined) updateData.attendees = updates.attendees;
    if (updates.leads !== undefined) updateData.leads = updates.leads;
    if (updates.status !== undefined) updateData.status = updates.status;

    await updateDoc(docRef, updateData);
  }

  // ========== Lead Sources ==========

  static async getAllLeadSources(): Promise<LeadSourceRecord[]> {
    const q = query(
      collection(db, LEAD_SOURCES_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToLeadSource(doc.id, doc.data()));
  }

  static async createLeadSource(sourceData: Partial<LeadSourceRecord>): Promise<string> {
    const now = Timestamp.now();
    const data: any = {
      name: sourceData.name || '',
      type: sourceData.type || 'other',
      description: sourceData.description || null,
      campaignId: sourceData.campaignId || null,
      url: sourceData.url || null,
      utmParameters: sourceData.utmParameters || {},
      isActive: sourceData.isActive !== undefined ? sourceData.isActive : true,
      createdAt: now,
      lastUpdated: now,
    };

    const docRef = await addDoc(collection(db, LEAD_SOURCES_COLLECTION), data);
    return docRef.id;
  }

  // ========== Helper Methods ==========

  private static docToCampaign(id: string, data: any): MarketingCampaign {
    return {
      id,
      name: data.name || '',
      type: data.type || CampaignType.email,
      status: data.status || CampaignStatus.draft,
      description: data.description,
      startDate: data.startDate?.toDate(),
      endDate: data.endDate?.toDate(),
      budget: data.budget,
      spent: data.spent || 0,
      targetAudience: data.targetAudience,
      goals: data.goals || [],
      metrics: data.metrics || {},
      createdBy: data.createdBy || '',
      createdByName: data.createdByName || '',
      createdAt: data.createdAt?.toDate() || new Date(),
      lastUpdated: data.lastUpdated?.toDate() || new Date(),
    };
  }

  private static docToEmailCampaign(id: string, data: any): EmailCampaign {
    return {
      id,
      campaignId: data.campaignId,
      name: data.name || '',
      subject: data.subject || '',
      fromEmail: data.fromEmail || '',
      fromName: data.fromName || '',
      templateId: data.templateId,
      htmlContent: data.htmlContent || '',
      textContent: data.textContent,
      status: data.status || EmailStatus.draft,
      scheduledAt: data.scheduledAt?.toDate(),
      sentAt: data.sentAt?.toDate(),
      recipientCount: data.recipientCount || 0,
      deliveredCount: data.deliveredCount || 0,
      openedCount: data.openedCount || 0,
      clickedCount: data.clickedCount || 0,
      bouncedCount: data.bouncedCount || 0,
      unsubscribedCount: data.unsubscribedCount || 0,
      segments: data.segments || [],
      tags: data.tags || [],
      createdBy: data.createdBy || '',
      createdByName: data.createdByName || '',
      createdAt: data.createdAt?.toDate() || new Date(),
      lastUpdated: data.lastUpdated?.toDate() || new Date(),
    };
  }

  private static docToEmailTemplate(id: string, data: any): EmailTemplate {
    return {
      id,
      name: data.name || '',
      subject: data.subject || '',
      htmlContent: data.htmlContent || '',
      textContent: data.textContent,
      category: data.category,
      variables: data.variables || [],
      isActive: data.isActive !== undefined ? data.isActive : true,
      createdBy: data.createdBy || '',
      createdByName: data.createdByName || '',
      createdAt: data.createdAt?.toDate() || new Date(),
      lastUpdated: data.lastUpdated?.toDate() || new Date(),
    };
  }

  private static docToContentPost(id: string, data: any): ContentPost {
    return {
      id,
      title: data.title || '',
      type: data.type || 'blog',
      content: data.content,
      url: data.url,
      platform: data.platform,
      status: data.status || 'draft',
      scheduledAt: data.scheduledAt?.toDate(),
      publishedAt: data.publishedAt?.toDate(),
      author: data.author || '',
      authorName: data.authorName || '',
      tags: data.tags || [],
      metrics: data.metrics || {},
      createdAt: data.createdAt?.toDate() || new Date(),
      lastUpdated: data.lastUpdated?.toDate() || new Date(),
    };
  }

  private static docToEvent(id: string, data: any): Event {
    return {
      id,
      name: data.name || '',
      type: data.type || 'other',
      description: data.description,
      startDate: data.startDate?.toDate() || new Date(),
      endDate: data.endDate?.toDate() || new Date(),
      location: data.location,
      virtualUrl: data.virtualUrl,
      registrationUrl: data.registrationUrl,
      budget: data.budget,
      spent: data.spent || 0,
      registrations: data.registrations || 0,
      attendees: data.attendees || 0,
      leads: data.leads || 0,
      status: data.status || 'planned',
      createdBy: data.createdBy || '',
      createdByName: data.createdByName || '',
      createdAt: data.createdAt?.toDate() || new Date(),
      lastUpdated: data.lastUpdated?.toDate() || new Date(),
    };
  }

  private static docToLeadSource(id: string, data: any): LeadSourceRecord {
    return {
      id,
      name: data.name || '',
      type: data.type || 'other',
      description: data.description,
      campaignId: data.campaignId,
      url: data.url,
      utmParameters: data.utmParameters || {},
      isActive: data.isActive !== undefined ? data.isActive : true,
      createdAt: data.createdAt?.toDate() || new Date(),
      lastUpdated: data.lastUpdated?.toDate() || new Date(),
    };
  }
}

